import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";


export default function SignaturePad({
  editable = false,
  saving = false,
  initialCustomerName = "",
  signedAt = null,
  onSave,
}) {
  const canvasRef =
    useRef(null);

  const drawingRef =
    useRef(false);

  const hasDrawingRef =
    useRef(false);

  const [
    customerName,
    setCustomerName,
  ] = useState(
    initialCustomerName
  );


  useEffect(() => {
    setCustomerName(
      initialCustomerName || ""
    );
  }, [initialCustomerName]);


  useEffect(() => {
    const canvas =
      canvasRef.current;

    if (!canvas) {
      return;
    }

    const context =
      canvas.getContext("2d");

    context.fillStyle = "#ffffff";

    context.fillRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    context.strokeStyle =
      "#111827";

    context.lineWidth = 3;
    context.lineCap = "round";
    context.lineJoin = "round";

    hasDrawingRef.current =
      false;
  }, []);


  const pointerPosition = (
    event
  ) => {
    const canvas =
      canvasRef.current;

    const rectangle =
      canvas.getBoundingClientRect();

    return {
      x:
        (
          event.clientX -
          rectangle.left
        ) *
        (
          canvas.width /
          rectangle.width
        ),
      y:
        (
          event.clientY -
          rectangle.top
        ) *
        (
          canvas.height /
          rectangle.height
        ),
    };
  };


  const startDrawing = (
    event
  ) => {
    if (
      !editable ||
      saving
    ) {
      return;
    }

    const canvas =
      canvasRef.current;

    canvas.setPointerCapture(
      event.pointerId
    );

    const context =
      canvas.getContext("2d");

    const point =
      pointerPosition(event);

    context.beginPath();

    context.moveTo(
      point.x,
      point.y
    );

    drawingRef.current = true;
  };


  const draw = (
    event
  ) => {
    if (
      !drawingRef.current ||
      !editable ||
      saving
    ) {
      return;
    }

    const canvas =
      canvasRef.current;

    const context =
      canvas.getContext("2d");

    const point =
      pointerPosition(event);

    context.lineTo(
      point.x,
      point.y
    );

    context.stroke();

    hasDrawingRef.current =
      true;
  };


  const stopDrawing = () => {
    drawingRef.current = false;
  };


  const clear = () => {
    const canvas =
      canvasRef.current;

    const context =
      canvas.getContext("2d");

    context.clearRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    context.fillStyle = "#ffffff";

    context.fillRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    context.strokeStyle =
      "#111827";

    context.lineWidth = 3;
    context.lineCap = "round";
    context.lineJoin = "round";

    hasDrawingRef.current =
      false;
  };


  const save = () => {
    const cleanName =
      customerName.trim();

    if (!cleanName) {
      toast.error(
        "Renseignez le nom du signataire."
      );

      return;
    }

    if (
      !hasDrawingRef.current
    ) {
      toast.error(
        "Ajoutez la signature du client."
      );

      return;
    }

    canvasRef.current.toBlob(
      (blob) => {
        if (!blob) {
          toast.error(
            "Impossible de générer la signature."
          );

          return;
        }

        const file = new File(
          [blob],
          "signature-client.png",
          {
            type: "image/png",
          }
        );

        onSave(
          file,
          cleanName
        );
      },
      "image/png"
    );
  };


  return (
    <div
      data-testid="signature-pad"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
        <div>
          <label
            htmlFor="signature-customer-name"
            className="label-eyebrow mb-1.5 block"
          >
            Nom du signataire
          </label>

          <input
            id="signature-customer-name"
            type="text"
            value={customerName}
            onChange={(event) =>
              setCustomerName(
                event.target.value
              )
            }
            disabled={
              !editable ||
              saving
            }
            maxLength={160}
            data-testid="signature-customer-name"
            className="w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm disabled:opacity-70"
          />
        </div>

        {signedAt && (
          <div className="self-end rounded-lg border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-sm text-[var(--do-accent)]">
            Signature enregistrée
          </div>
        )}
      </div>

      <div className="mt-3 overflow-hidden rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
        <canvas
          ref={canvasRef}
          width={900}
          height={240}
          onPointerDown={
            startDrawing
          }
          onPointerMove={draw}
          onPointerUp={
            stopDrawing
          }
          onPointerCancel={
            stopDrawing
          }
          onPointerLeave={
            stopDrawing
          }
          data-testid="signature-canvas"
          className={`block h-[220px] w-full touch-none ${
            editable
              ? "cursor-crosshair"
              : "cursor-not-allowed opacity-70"
          }`}
        />
      </div>

      {editable && (
        <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={clear}
            disabled={saving}
            data-testid="signature-clear"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-heading font-semibold disabled:opacity-50"
          >
            <X size={16} />

            Effacer
          </button>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="signature-save"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
          >
            <Check
              size={17}
              weight="bold"
            />

            {saving
              ? "Enregistrement…"
              : "Enregistrer la signature"}
          </button>
        </div>
      )}
    </div>
  );
}
